// The import template's field registry — the SINGLE source for the downloadable
// template's header row and the prep prompt's column list (PE-1959).
//
// Sources:
//   docs/initiatives/new/dashboard-import-tool/SPEC.md
//     §4.0  ("the prompt, the template, and the mapping engine's field enum are
//            generated from one field registry")
//     §4.0.1 (the tier classifier's header-set comparison — see TEMPLATE_HEADERS)
//     §4.0.2 (DD/MM vs MM/DD is unresolvable from data alone)
//   docs/initiatives/new/dashboard-import-tool/import-engine-contract.md
//     §4.2  (obligation 1 — every header carries a decision)
//   docs/initiatives/new/dashboard-import-tool/assets/contact-import-template.csv
//     (the committed asset this registry must reproduce byte-for-byte)
//   docs/initiatives/new/dashboard-import-tool/decisions.md#D-TEMPLATE-REGISTRY
//
// WHY THIS FILE EXISTS. SPEC §4.0's fourth prompt constraint: "Three
// hand-maintained copies drift inside a release and the prompt starts emitting
// columns we no longer accept." The template's header row, the prompt's column
// list, and the tier classifier's Tier-0 reference set are all projections of
// THIS array. A test in this package fails if any two of them disagree.
//
// THIS IS ADDITIVE, NOT A REDEFINITION. `fields.ts` is a transcription and is
// not edited by this module — no catalog member is renamed, removed, or
// reordered. This file only PROJECTS from `TARGET_FIELD_KEYS`, and the
// `assertRegistryCoversCatalog` invariant below makes an unprojected catalog
// member a build break rather than a silent omission.
//
// V1 SHIPS THE STANDARD (ORG-INDEPENDENT) TEMPLATE. Per-org enrichment — a
// column per custom-field definition, and the org's tag vocabulary — is
// PE-1985. SPEC §4.0 item 2 describes the per-org form as the eventual shape;
// V1's narrower cut and its rationale are recorded in
// `decisions.md#D-TEMPLATE-REGISTRY`. The registry is built so that enrichment
// is an APPEND to `TEMPLATE_COLUMNS`, not a retrofit of it.

import {
  TARGET_FIELD_KEYS,
  type ColumnTarget,
  type TargetFieldKey,
} from './fields.js';

// ---------------------------------------------------------------------------
// The column shape
// ---------------------------------------------------------------------------

/**
 * One column of the standard template, in template order.
 *
 * `target` is the frozen `ColumnTarget` union from annex §4.2 — a catalog
 * member or one of the three routing sentinels. Deliberately NOT widened to
 * `string`: a free-text destination here would be a new write target smuggled
 * past review, which is what obligation 2's "never free text" forbids.
 *
 * `fieldKey` is present only on catalog-backed columns and is what lets the
 * coverage invariant below run. A sentinel column has `fieldKey: null` — it is
 * a real declared destination, just not a catalog field.
 */
export interface TemplateColumn {
  /** The header as it appears in the user's spreadsheet. snake_case. */
  readonly templateHeader: string;
  /** Where this column's values are routed at import. */
  readonly target: ColumnTarget;
  /** The catalog member this column projects, or `null` for a sentinel column. */
  readonly fieldKey: TargetFieldKey | null;
  /** Why this column exists / what a reader must not "tidy up". */
  readonly note?: string;
}

/**
 * A catalog member that deliberately has NO template column.
 *
 * WHY THIS TYPE EXISTS AT ALL. PE-1959's acceptance criterion was written as
 * "a test fails if a `TargetFieldKey` exists with no `templateHeader`". Taken
 * literally that test fails against untouched merged code, because `fullName`
 * is a legitimate catalog member that correctly has no template column (see
 * `TEMPLATE_EXCLUSIONS`). The criterion's PURPOSE — no catalog member is ever
 * silently absent from the template — is preserved exactly by requiring every
 * member to carry a DECLARED DECISION: a header, or an exclusion with a
 * written reason. Absence remains illegal.
 *
 * This is the same discipline annex §4.2 obligation 1 already applies to
 * columns: "absence is NEVER a way to express a decision."
 */
export interface TemplateExclusion {
  readonly fieldKey: TargetFieldKey;
  readonly reason: string;
}

// ---------------------------------------------------------------------------
// The registry
// ---------------------------------------------------------------------------

/**
 * The standard template's columns, IN TEMPLATE ORDER.
 *
 * Order is load-bearing twice over: it is the header row `generateTemplate()`
 * emits, and it is the column list the prep prompt tells the assistant to
 * produce ("exactly these columns, in this order"). Reordering this array
 * silently reorders both.
 *
 * HEADER SPELLING IS THE ASSET'S, NOT THE CATALOG'S. The catalog is
 * wizard-facing camelCase (`postalCode`, `organizationName`); template headers
 * are what a user reads in their spreadsheet, and the committed asset already
 * fixed those spellings (`zip`, `company`). This registry is the ONE place that
 * projection lives — do not re-derive it with a camelCase→snake_case function,
 * because three of these are not mechanical transforms:
 *
 *   postalCode        → zip                     (not `postal_code`)
 *   organizationName  → company                 (not `organization_name`)
 *   addressLine1      → address_line_1          (digit is separated)
 *
 * A test asserts this array reproduces the committed asset's header row
 * byte-for-byte, so drift in either direction fails the build.
 */
const TEMPLATE_COLUMNS_SOURCE = [
  { templateHeader: 'first_name', target: 'firstName', fieldKey: 'firstName' },
  { templateHeader: 'last_name', target: 'lastName', fieldKey: 'lastName' },
  { templateHeader: 'email', target: 'email', fieldKey: 'email' },
  { templateHeader: 'phone', target: 'phone', fieldKey: 'phone' },
  {
    templateHeader: 'address_line_1',
    target: 'addressLine1',
    fieldKey: 'addressLine1',
  },
  {
    templateHeader: 'address_line_2',
    target: 'addressLine2',
    fieldKey: 'addressLine2',
  },
  { templateHeader: 'city', target: 'city', fieldKey: 'city' },
  { templateHeader: 'state', target: 'state', fieldKey: 'state' },
  {
    templateHeader: 'zip',
    target: 'postalCode',
    fieldKey: 'postalCode',
    note: 'Header is `zip`, not `postal_code` — the asset\'s spelling. SPEC §5.4.3 renames to `postal` in the WRITE path, not here.',
  },
  { templateHeader: 'country', target: 'country', fieldKey: 'country' },
  {
    templateHeader: 'company',
    target: 'organizationName',
    fieldKey: 'organizationName',
    note: 'Header is `company` (the asset\'s spelling); the catalog key is `organizationName`, made first-class in PE-1912.',
  },
  {
    templateHeader: 'email_consent',
    target: 'emailConsent',
    fieldKey: 'emailConsent',
    note: 'Values are the §9.1 vocabulary (`opted_in` / `opted_out` / `needs_review` / `no_data`). A mapped consent column still passes the §9.3 confirm gate.',
  },
  {
    templateHeader: 'sms_consent',
    target: 'smsConsent',
    fieldKey: 'smsConsent',
    note:
      'SMS carries `opted_in` / `opted_out` / `no_data` (SMS_CONSENT_VALUES) — ' +
      'note the asymmetry with email: there is deliberately NO `needs_review` ' +
      'for SMS, because an unresolved SMS state has nowhere to live on the row ' +
      'and routes to a triage flag instead. `opted_in` became legal when ' +
      'OVERVIEW Open Q3 was ratified (decisions.md#D-SMS-OPT-IN-GRANT); a value ' +
      'here records the ROW\'S CLAIM and does not by itself grant consent — the ' +
      'import additionally requires a job-level SMS attestation and denies the ' +
      'grant without one.',
  },
  {
    templateHeader: 'tags',
    target: 'TAG',
    fieldKey: null,
    note: 'Semicolon-separated in one cell. PE-1985 enriches this same column with the org\'s tag vocabulary — an append, not a replacement.',
  },
  { templateHeader: 'external_id', target: 'externalId', fieldKey: 'externalId' },
  {
    templateHeader: 'date_added (YYYY-MM-DD)',
    target: 'CUSTOM_FIELD',
    fieldKey: null,
    note:
      'THE `(YYYY-MM-DD)` IN THE HEADER IS LOAD-BEARING — §4.0.2 names DD/MM vs ' +
      'MM/DD as unresolvable from data alone, so the header removes the ambiguity ' +
      'at source. Routed to CUSTOM_FIELD explicitly rather than left to fall to ' +
      'SKIP: annex §4.2 obligation 1 makes every header carry a decision, and a ' +
      'default-SKIP here would silently discard the date.',
  },
] as const;

/**
 * `TEMPLATE_COLUMNS_SOURCE`, deep-frozen.
 *
 * `as const` is compile-time only — it does not stop a runtime `any`-typed
 * caller from mutating an entry in place (verified: `(TEMPLATE_COLUMNS[0] as
 * any).templateHeader = 'x'` silently succeeds without this). `generateTemplate()`
 * reads `TEMPLATE_COLUMNS` live on every call for the example row, so an
 * in-place mutation would silently change output on the NEXT call without
 * `collectRegistryDefects()` ever re-running to catch it. Freezing both the
 * array and every element closes that gap, matching the freezing convention
 * already used by `TEMPLATE_HEADER_TARGETS` / `TEMPLATE_HEADER_BY_FIELD_KEY`
 * below.
 */
export const TEMPLATE_COLUMNS: readonly TemplateColumn[] = Object.freeze(
  TEMPLATE_COLUMNS_SOURCE.map((column) => Object.freeze({ ...column })),
);

/**
 * Catalog members with no template column, each with its reason.
 *
 * Every entry here is a decision someone made on purpose. Adding a member to
 * this list is how you say "the template does not ask for this"; leaving a
 * member out of BOTH this list and `TEMPLATE_COLUMNS` is a build break.
 */
export const TEMPLATE_EXCLUSIONS: readonly TemplateExclusion[] = Object.freeze(
  [
    {
      fieldKey: 'fullName',
      reason:
        'A mapping target with no template column. The mapping contract requires ' +
        '`firstName` + `lastName`, and a file arriving with a single name-ish column ' +
        'is resolved by a loader-side split BEFORE mapping sees it — so the template ' +
        'asks for the decomposed form the requirement names. Offering `full_name` ' +
        'alongside `first_name`/`last_name` would invite a user to fill all three and ' +
        'create a tiebreak we would have to invent. `fullName` REMAINS a valid mapping ' +
        'destination: it serves the fallback path (a declined or absent split) and any ' +
        'file whose name column we cannot decompose. Do not "fix" this by adding a ' +
        'template column. See decisions.md#D-TEMPLATE-REGISTRY.',
    } as const,
  ].map((exclusion) => Object.freeze({ ...exclusion })),
);

// ---------------------------------------------------------------------------
// Projections
// ---------------------------------------------------------------------------

/**
 * The template's header row, in order.
 *
 * ⚠️ THE TIER CLASSIFIER (PE-1960) MUST READ THIS, NOT `TARGET_FIELD_KEYS`.
 * SPEC §4.0.1 classifies an uploaded file by comparing its normalized header
 * set against *the template's* headers. Comparing against the catalog instead
 * would expect a `full_name` column no template ever emits, and NO file would
 * ever classify as Tier 0.
 *
 * Two further notes for that classifier, both from the same §4.0.1 paragraph:
 *
 *  1. Normalization is the classifier's job, not this array's. These strings
 *     are the VERBATIM headers a user sees — including the parenthetical in
 *     `date_added (YYYY-MM-DD)`, which any normalizer must strip before
 *     comparing (lowercase, strip punctuation, collapse whitespace).
 *  2. If the loader-side name split lands, it rewrites headers BEFORE
 *     classification runs — so a one-name-column file may reach the classifier
 *     already carrying `first_name`/`last_name`. Classify post-split.
 */
export const TEMPLATE_HEADERS: readonly string[] = TEMPLATE_COLUMNS.map(
  (column) => column.templateHeader,
);

/** `templateHeader` → its declared destination. The mapping the classifier identity-maps with. */
export const TEMPLATE_HEADER_TARGETS: Readonly<Record<string, ColumnTarget>> =
  Object.freeze(
    Object.fromEntries(
      TEMPLATE_COLUMNS.map((column) => [column.templateHeader, column.target]),
    ),
  );

/** `TargetFieldKey` → its template header, for the catalog-backed columns only. */
export const TEMPLATE_HEADER_BY_FIELD_KEY: Readonly<
  Partial<Record<TargetFieldKey, string>>
> = Object.freeze(
  Object.fromEntries(
    TEMPLATE_COLUMNS.filter(
      (column): column is TemplateColumn & { fieldKey: TargetFieldKey } =>
        column.fieldKey !== null,
    ).map((column) => [column.fieldKey, column.templateHeader]),
  ),
);

// ---------------------------------------------------------------------------
// The coverage invariant
// ---------------------------------------------------------------------------

/** A single way the registry can be inconsistent, as a human-readable string. */
export type RegistryDefect = string;

/**
 * Every way this registry can be internally inconsistent, as a list of
 * defects. Empty means consistent.
 *
 * Exported as a FUNCTION rather than run at module load on purpose: a contract
 * package that throws at import time would take down every consumer of every
 * other schema in the barrel over a template typo. The package's test suite
 * calls this and fails the build; runtime consumers are unaffected.
 *
 * The checks, in the order PE-1959's acceptance criteria name them:
 *
 *  1. TOTALITY — every `TargetFieldKey` carries a declared decision (a column
 *     or an exclusion). This is the criterion "adding a catalog member without
 *     a header is a build break, not a silent omission", read as "without a
 *     DECISION" so that `fullName`'s deliberate exclusion is expressible.
 *  2. No member is BOTH mapped and excluded — a contradiction, not a decision.
 *  3. No exclusion names a non-existent catalog member (catches a rename).
 *  4. No column targets `SKIP`. A template column that discards its own values
 *     is never correct; `tags` and `date_added` in particular must not degrade
 *     to SKIP, which is PE-1959's third acceptance criterion.
 *  5. Headers are unique — a duplicate header makes the CSV ambiguous and
 *     collapses in `TEMPLATE_HEADER_TARGETS`.
 *  6. Catalog-backed columns are unique per field key, and a column's `target`
 *     agrees with its own `fieldKey` (catches a copy-paste that points two
 *     headers at one field).
 *  7. Headers are non-empty and free of the CSV/spreadsheet hazards a header
 *     row must not contain (a comma, a quote, a newline).
 */
export function collectRegistryDefects(): RegistryDefect[] {
  return collectRegistryDefectsFor(TEMPLATE_COLUMNS, TEMPLATE_EXCLUSIONS);
}

/**
 * The same checks as {@link collectRegistryDefects}, parameterized over the
 * columns/exclusions to check.
 *
 * EXPORTED SO TESTS CAN EXERCISE EVERY BRANCH. `collectRegistryDefects()` runs
 * against the real registry, which is (by construction) already valid — so a
 * test suite that only calls the no-arg form can never observe the checker
 * actually catching anything; every `defects.push(...)` call would be
 * unverified dead code that a regression could silently break. This overload
 * lets the test suite hand it small, deliberately-broken synthetic arrays and
 * assert the specific defect string comes back.
 */
export function collectRegistryDefectsFor(
  columns: readonly TemplateColumn[],
  exclusions: readonly TemplateExclusion[],
): RegistryDefect[] {
  const defects: RegistryDefect[] = [];

  const mappedKeys = new Set<string>();
  const seenHeaders = new Set<string>();

  for (const column of columns) {
    const { templateHeader, target, fieldKey } = column;

    if (templateHeader.trim() === '') {
      defects.push('a template column has an empty header');
    }
    if (/[,"\r\n]/.test(templateHeader)) {
      defects.push(
        `header ${JSON.stringify(templateHeader)} contains a comma, quote, or newline — ` +
          'a template header row must be writable without CSV escaping',
      );
    }
    if (seenHeaders.has(templateHeader)) {
      defects.push(`duplicate template header: ${templateHeader}`);
    }
    seenHeaders.add(templateHeader);

    if (target === 'SKIP') {
      defects.push(
        `template column ${templateHeader} targets SKIP — a template column must ` +
          'never discard its own values (annex §4.2 obligation 1)',
      );
    }

    if (fieldKey === null) {
      // A sentinel column. Its target must actually be a sentinel; a catalog
      // key with a null fieldKey would escape the totality check below.
      if (target !== 'CUSTOM_FIELD' && target !== 'TAG') {
        defects.push(
          `template column ${templateHeader} has fieldKey null but targets ` +
            `${target}, which is a catalog member — set fieldKey to match`,
        );
      }
      continue;
    }

    if (target !== fieldKey) {
      defects.push(
        `template column ${templateHeader} declares fieldKey ${fieldKey} but ` +
          `targets ${target} — the two must agree`,
      );
    }
    if (mappedKeys.has(fieldKey)) {
      defects.push(`catalog member ${fieldKey} is claimed by two template columns`);
    }
    mappedKeys.add(fieldKey);
  }

  const excludedKeys = new Set<string>();
  for (const { fieldKey, reason } of exclusions) {
    if (!(TARGET_FIELD_KEYS as readonly string[]).includes(fieldKey)) {
      defects.push(
        `exclusion names ${fieldKey}, which is not a member of TARGET_FIELD_KEYS`,
      );
    }
    if (excludedKeys.has(fieldKey)) {
      defects.push(`catalog member ${fieldKey} is excluded twice`);
    }
    if (reason.trim() === '') {
      defects.push(`exclusion of ${fieldKey} carries no reason`);
    }
    if (mappedKeys.has(fieldKey)) {
      defects.push(
        `catalog member ${fieldKey} is both mapped to a template column and ` +
          'excluded — that is a contradiction, not a decision',
      );
    }
    excludedKeys.add(fieldKey);
  }

  for (const key of TARGET_FIELD_KEYS) {
    if (!mappedKeys.has(key) && !excludedKeys.has(key)) {
      defects.push(
        `catalog member ${key} carries no declared decision — give it a ` +
          'templateHeader in TEMPLATE_COLUMNS, or an explicit entry in ' +
          'TEMPLATE_EXCLUSIONS with a reason',
      );
    }
  }

  return defects;
}
